// admin/src/pages/AdminSpotlight.jsx
import React, { useState, useEffect, useContext } from 'react';
import { Save, Plus, Trash2, Eye } from 'lucide-react';
import { AuthContext } from '../AuthContext';
import { safeUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const font = {
  mono:      '"JetBrains Mono", monospace',
  condensed: '"Barlow Condensed", sans-serif',
  body:      '"Barlow", sans-serif',
};

const EMPTY = {
  name: '', title: '', image_url: '', quote: '', bio: '',
  achievements: [''],
  stat1_value: '', stat1_label: '',
  stat2_value: '', stat2_label: '',
  stat3_value: '', stat3_label: '',
  social_twitch: '', social_twitter: '', social_youtube: '',
  social_instagram: '', social_tiktok: '', social_website: '',
};

const AdminSpotlight = () => {
  const { authToken } = useContext(AuthContext);
  const [form,        setForm]        = useState(EMPTY);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [toast,       setToast]       = useState('');
  const [spotlightId, setSpotlightId] = useState(null);

  const headers  = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };
  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/spotlight`, { headers })
      .then(r => r.json())
      .then(data => {
        if (data.spotlight) {
          const s = data.spotlight;
          setSpotlightId(s.id);
          setForm({
            name: s.name||'', title: s.title||'', image_url: s.image_url||'',
            quote: s.quote||'', bio: s.bio||'',
            achievements: s.achievements?.length ? s.achievements : [''],
            stat1_value: s.stat1_value||'', stat1_label: s.stat1_label||'',
            stat2_value: s.stat2_value||'', stat2_label: s.stat2_label||'',
            stat3_value: s.stat3_value||'', stat3_label: s.stat3_label||'',
            social_twitch: s.social_twitch||'', social_twitter: s.social_twitter||'',
            social_youtube: s.social_youtube||'', social_instagram: s.social_instagram||'',
            social_tiktok: s.social_tiktok||'', social_website: s.social_website||'',
          });
        }
      })
      .catch(() => showToast('Failed to load spotlight'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const setAchievement    = (i, val) => { const a = [...form.achievements]; a[i] = val; set('achievements', a); };
  const addAchievement    = ()       => set('achievements', [...form.achievements, '']);
  const removeAchievement = i        => set('achievements', form.achievements.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim()) { showToast('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, achievements: form.achievements.filter(a => a.trim()) };
      let res;
      if (spotlightId) {
        res = await fetch(`${BACKEND_URL}/api/admin/spotlight/${spotlightId}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
      } else {
        res = await fetch(`${BACKEND_URL}/api/admin/spotlight`, { method: 'POST', headers, body: JSON.stringify(payload) });
        const data = await res.json();
        if (data.spotlight?.id) setSpotlightId(data.spotlight.id);
      }
      if (!res.ok) throw new Error();
      showToast('✓ Spotlight saved — live on homepage now!');
    } catch { showToast('Failed to save'); }
    finally { setSaving(false); }
  };

  const S = {
    page:     { padding: '2.5rem', maxWidth: 900 },
    section:  { background: '#0a0a0a', border: '1px solid #111', padding: '1.5rem', marginBottom: '1.25rem' },
    secTitle: { fontFamily: font.mono, fontSize: '0.58rem', color: '#444', letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1.25rem' },
    label:    { fontFamily: font.mono, fontSize: '0.55rem', color: '#444', letterSpacing: '0.12em', marginBottom: 5, display: 'block', textTransform: 'uppercase' },
    input:    { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.mono, fontSize: '0.7rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
    textarea: { background: '#060606', border: '1px solid #1a1a1a', color: '#fff', padding: '0.58rem 0.85rem', fontFamily: font.body, fontSize: '0.82rem', outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 },
    btnSave:  { background: '#fff', color: '#000', border: 'none', padding: '0.7rem 2rem', fontFamily: font.mono, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 },
    btnGhost: { background: 'none', border: '1px solid #1a1a1a', color: '#666', padding: '0.3rem 0.7rem', fontFamily: font.mono, fontSize: '0.6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 },
    btnAdd:   { background: 'none', border: '1px dashed #2a2a2a', color: '#555', padding: '0.4rem 0.9rem', fontFamily: font.mono, fontSize: '0.6rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6 },
  };

  if (loading) return <div style={{ padding: '2.5rem', fontFamily: font.mono, fontSize: '0.68rem', color: '#333' }}>LOADING...</div>;

  return (
    <div style={S.page}>
      <style>{`@keyframes fadein{from{opacity:0}to{opacity:1}} input:focus,textarea:focus{border-color:#333!important;}`}</style>

      {toast && (
        <div style={{ position:'fixed', bottom:'1.5rem', right:'1.5rem', background:'#111', border:'1px solid #222', color:'#fff', fontFamily:font.mono, fontSize:'0.7rem', padding:'0.75rem 1.25rem', zIndex:999, animation:'fadein 0.2s ease', maxWidth:360 }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:'2rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <div style={{ fontFamily:font.mono, fontSize:'0.58rem', color:'#333', letterSpacing:'0.2em', marginBottom:6 }}>// MANAGE</div>
          <div style={{ fontFamily:font.condensed, fontWeight:900, fontSize:'2.5rem', letterSpacing:'-0.02em' }}>Spotlight</div>
          <p style={{ fontFamily:font.body, fontWeight:300, fontSize:'0.82rem', color:'#444', marginTop:4 }}>Edit each section independently. Save when ready — goes live immediately.</p>
        </div>
        <div style={{ display:'flex', gap:'0.75rem', alignItems:'center' }}>
          <a href={process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000'} target="_blank" rel="noopener noreferrer" style={{ ...S.btnGhost, textDecoration:'none' }}><Eye size={11}/> Preview Site</a>
          <button style={S.btnSave} onClick={save} disabled={saving}><Save size={14}/>{saving?'SAVING...':'SAVE SPOTLIGHT'}</button>
        </div>
      </div>

      {/* Identity */}
      <div style={S.section}>
        <div style={S.secTitle}>// Identity</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem', marginBottom:'1rem' }}>
          <div><label style={S.label}>Full Name *</label><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. RAWKlickz"/></div>
          <div><label style={S.label}>Title / Role</label><input style={S.input} value={form.title} onChange={e=>set('title',e.target.value)} placeholder="e.g. Streamer & BGN Community Leader"/></div>
        </div>
        <div>
          <label style={S.label}>Photo URL</label>
          <input style={S.input} value={form.image_url} onChange={e=>set('image_url',e.target.value)} placeholder="Paste any image URL — https://..."/>
          {form.image_url && (
            <div style={{ marginTop:8, display:'flex', alignItems:'center', gap:10 }}>
              {safeUrl(form.image_url) && <img src={safeUrl(form.image_url)} alt="preview" style={{ width:64, height:64, objectFit:'cover', border:'1px solid #222' }} onError={e=>e.target.style.display='none'}/>}
              <span style={{ fontFamily:font.mono, fontSize:'0.6rem', color:'#444' }}>Image preview</span>
            </div>
          )}
        </div>
      </div>

      {/* Story */}
      <div style={S.section}>
        <div style={S.secTitle}>// Story</div>
        <div style={{ marginBottom:'1rem' }}>
          <label style={S.label}>Featured Quote</label>
          <textarea style={{ ...S.textarea, height:80 }} value={form.quote} onChange={e=>set('quote',e.target.value)} placeholder='"Write an inspiring quote that represents this person..."'/>
        </div>
        <div>
          <label style={S.label}>Bio / Description</label>
          <textarea style={{ ...S.textarea, height:130 }} value={form.bio} onChange={e=>set('bio',e.target.value)} placeholder="Write about their journey, impact, and story in the BGN community..."/>
        </div>
      </div>

      {/* Achievements */}
      <div style={S.section}>
        <div style={S.secTitle}>// Achievements</div>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {form.achievements.map((a, i) => (
            <div key={i} style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input style={{ ...S.input, flex:1 }} value={a} onChange={e=>setAchievement(i,e.target.value)} placeholder={`Achievement ${i+1} — e.g. 3x Tournament Champion`}/>
              {form.achievements.length > 1 && (
                <button style={{ ...S.btnGhost, color:'#ff6b6b', borderColor:'#2a1010', flexShrink:0 }} onClick={()=>removeAchievement(i)}><Trash2 size={11}/></button>
              )}
            </div>
          ))}
        </div>
        <button style={S.btnAdd} onClick={addAchievement}><Plus size={11}/> Add Achievement</button>
      </div>

      {/* Stats */}
      <div style={S.section}>
        <div style={S.secTitle}>// Highlight Stats (3 numbers shown below the spotlight)</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:'1rem' }}>
          {[1,2,3].map(n => (
            <div key={n} style={{ background:'#060606', border:'1px solid #1a1a1a', padding:'1rem' }}>
              <div style={{ fontFamily:font.mono, fontSize:'0.55rem', color:'#333', letterSpacing:'0.15em', marginBottom:'0.75rem' }}>STAT {n}</div>
              <div style={{ marginBottom:8 }}><label style={S.label}>Value</label><input style={S.input} value={form[`stat${n}_value`]} onChange={e=>set(`stat${n}_value`,e.target.value)} placeholder="e.g. 3 or 50+"/></div>
              <div><label style={S.label}>Label</label><input style={S.input} value={form[`stat${n}_label`]} onChange={e=>set(`stat${n}_label`,e.target.value)} placeholder="e.g. Tournament Wins"/></div>
            </div>
          ))}
        </div>
      </div>

      {/* Social Links */}
      <div style={S.section}>
        <div style={S.secTitle}>// Social Links</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.85rem' }}>
          {[
            { key:'social_twitch',    label:'🟣 Twitch',     ph:'https://twitch.tv/...' },
            { key:'social_twitter',   label:'𝕏 X / Twitter', ph:'https://x.com/...' },
            { key:'social_youtube',   label:'▶️ YouTube',     ph:'https://youtube.com/...' },
            { key:'social_instagram', label:'📸 Instagram',   ph:'https://instagram.com/...' },
            { key:'social_tiktok',    label:'🎵 TikTok',      ph:'https://tiktok.com/...' },
            { key:'social_website',   label:'🌐 Website',     ph:'https://...' },
          ].map(({ key, label, ph }) => (
            <div key={key}>
              <label style={S.label}>{label}</label>
              <input style={S.input} value={form[key]} onChange={e=>set(key,e.target.value)} placeholder={ph}/>
            </div>
          ))}
        </div>
      </div>

      {/* Save bottom */}
      <div style={{ display:'flex', justifyContent:'flex-end', paddingTop:'0.5rem' }}>
        <button style={S.btnSave} onClick={save} disabled={saving}><Save size={14}/>{saving?'SAVING...':'SAVE SPOTLIGHT'}</button>
      </div>
    </div>
  );
};

export default AdminSpotlight;